/**
 * The goodbye, shown over the gate after the session has already ended.
 *
 * It takes its content as props and reads no context, because by the time it
 * appears the platform's session is gone — which is the correct order, and the
 * reason this component knows nothing about where the numbers came from.
 *
 * The last line matters more than the greeting does. An operator leaving at the
 * end of a shift is handing an airfield to someone else, and what is still open
 * is the useful thing to say. "Goodbye" alone would be manners with nothing
 * behind it.
 */
import { useEffect, useRef } from "react";
import s from "./assistant.module.css";
import { AssistantMark } from "./AssistantMark";
import { farewell } from "./greeting";
import * as voice from "./voice";
import { Button } from "../design/components";
import { t } from "../i18n";
import type { SignOffSnapshot } from "./signOff";

export function Farewell({ snapshot, onClose }: { snapshot: SignOffSnapshot; onClose: () => void }) {
  /*
   * Spoken on mount. This component only ever mounts in response to the
   * sign-out press, so the page has the user activation a browser requires
   * before it will make a sound. It reads the goodbye alone, not the numbers —
   * a handover is read, not recited.
   */
  const said = useRef(false);
  useEffect(() => {
    if (said.current || !snapshot.speak) return;
    said.current = true;
    voice.speak(farewell(snapshot.name, new Date()), snapshot.voice);
  }, [snapshot]);

  const at = new Date(snapshot.at);
  const stamp = `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`;

  return (
    <div className={`${s.welcomeWrap} ${s.farewellWrap}`} role="presentation" onClick={onClose}>
      <div
        className={s.welcome}
        role="dialog"
        aria-modal="true"
        aria-labelledby="assistant-farewell"
        onClick={(e) => e.stopPropagation()}
      >
        <AssistantMark state="idle" size={48} />
        <p className={s.welcomeGreeting} id="assistant-farewell">
          {farewell(snapshot.name, new Date())}
        </p>
        {snapshot.lines.length > 0 && (
          <div className={s.welcomeFacts}>
            {snapshot.lines.map((l, i) => (
              <div key={i}>{l}</div>
            ))}
          </div>
        )}
        <p className={s.welcomeLine}>
          {t("You are signed out. The figures above were read at {stamp}, not now.", { stamp })}
        </p>
        <div className={s.welcomeActions}>
          <Button variant="primary" onClick={onClose}>
            {t("Close")}
          </Button>
        </div>
      </div>
    </div>
  );
}
